import React, { useCallback, useContext, useMemo } from 'react';
import bowser from 'yola-bowser';
import { useSelector, useDispatch } from 'react-redux';
import { dialogs, site, siteLocales, i18next, integration, view } from '@yola/ws-sdk';
import helpCenter from 'yola-editor/src/js/modules/help-center';
import { saveSiteWithEffect } from 'src/js/modules/saving/helpers/save-site';
import MultilingualSection from '../components/multilingual-section';
import dialogTypes from '../../dialogs/constants/dialog-types';
import getLanguages from '../helpers/get-languages';
import getCountries from '../helpers/get-countries';
import getUsedLanguages from '../helpers/get-used-languages';
import constants from '../constants';
import segment from '../../analytics/segment';
import getRelatedLanguageOptions from '../helpers/get-related-language-options';
import updateLanguageSelector from '../helpers/update-language-selector';
import WebsiteSettingsContext from '../context/website-settings-context';
import useCheckForUnsafe from '../hooks/use-check-for-unsafe';

const { tabIdentifiers } = constants;
const {
  trackers: { trackEvent },
  constants: { events },
} = segment;

const showSavingDialog = () => {
  dialogs.operations.show(dialogTypes.UPLOAD_PROGRESS_DIALOG, {
    captions: {
      title: i18next.t('Saving Website'),
      description: i18next.t('We are saving your website. Please wait.'),
    },
    overlay: 'visible',
    showProgress: true,
    cancelable: false,
    progressType: 'indeterminate',
  });
};

function MultilingualTabContainer() {
  const dispatch = useDispatch();
  const requestBlockData = useSelector(integration.selectors.getRequestBlock);

  const {
    showPromptForce,
    formValues: formData,
    onAfterDialogClose,
    captions: { multilingual: captions },
    areAiFeaturesAvailable,
  } = useContext(WebsiteSettingsContext);

  const locales = useMemo(() => siteLocales.accessors.getSiteLocales(), []);
  const mainLocale = useMemo(() => siteLocales.accessors.getMainLocale(), []);
  const languages = useMemo(() => getLanguages(), []);
  const countries = useMemo(() => getCountries(), []);
  const availableLocales = useMemo(
    () => getRelatedLanguageOptions(locales, languages, countries),
    [locales, languages, countries]
  );
  const tutorialUrl = useMemo(() => helpCenter.accessors.getMultilingualAiTutorialUrl(), []);

  const showMultilingualTab = useCallback(() => {
    dialogs.operations.show(dialogTypes.WEBSITE_SETTINGS, {
      activeTabId: tabIdentifiers.MULTILINGUAL,
      formData,
      showPromptForce,
      onAfterDialogClose,
    });
  }, [formData, showPromptForce, onAfterDialogClose]);

  const checkForUnsafe = useCheckForUnsafe();

  const handleAfterLanguageDeleted = useCallback(async () => {
    await updateLanguageSelector();
    await saveSiteWithEffect(() => {
      dispatch(view.actions.forceReloadView());
      showMultilingualTab();
    });
  }, [dispatch, showMultilingualTab]);

  const handleDeleteLocale = useCallback(async (languageCode) => {
    await siteLocales.operations.removeLocale(languageCode);
    trackEvent(events.WEBSITE_LANGUAGE_DELETED, { languageCode });
  }, []);

  const setMainLang = useCallback(
    (locale) => {
      dialogs.operations.show(dialogTypes.CONFIRM_DIALOG, {
        onCancel: showMultilingualTab,
        onSubmit: async () => {
          showSavingDialog();
          await siteLocales.operations.saveMainLocale(locale);
          await updateLanguageSelector();
          await saveSiteWithEffect(() => {
            dispatch(view.actions.forceReloadView());
            showMultilingualTab();
          });
          trackEvent(events.DEFAULT_WEBSITE_LANGUAGE_CHANGED, {
            oldLanguageCode: mainLocale,
            newLanguageCode: locale,
          });
        },
        captions: {
          title: i18next.t('Change main language?'),
          description: i18next.t('Are you sure you want to change the main language of your site?'),
          submit: i18next.t('Submit'),
          cancel: i18next.t('Cancel'),
        },
      });
    },
    [dispatch, mainLocale, showMultilingualTab]
  );

  const deleteLang = useCallback(
    (languageCode) => {
      dialogs.operations.show(dialogTypes.CONFIRM_DIALOG, {
        onCancel: showMultilingualTab,
        onSubmit: checkForUnsafe({
          onSubmit: async () => {
            showSavingDialog();
            await handleDeleteLocale(languageCode);
            saveSiteWithEffect(() => handleAfterLanguageDeleted());
          },
          onCancel: showMultilingualTab,
        }),
        captions: {
          title: i18next.t('Delete language?'),
          description: i18next.t(
            "You're about to delete a website language. All related content and pages will be deleted. Are you sure?"
          ),
          submit: i18next.t('Submit'),
          cancel: i18next.t('Cancel'),
        },
        height: 245,
      });
    },
    [checkForUnsafe, handleDeleteLocale, handleAfterLanguageDeleted, showMultilingualTab]
  );

  const addLanguage = useCallback(() => {
    const isMl = site.verifiers.isMultilingualSite();
    const isMultilingualIntroShown = site.verifiers.isMultilingualIntroShown();
    const roadmapUrl = requestBlockData ? requestBlockData.publicRoadmapUrl : null;

    const showAddLanguageModal = () => {
      dialogs.operations.show(dialogTypes.ADD_LANGUAGE, {
        onClose: showMultilingualTab,
      });
    };

    if (!isMl && !isMultilingualIntroShown) {
      dialogs.operations.show(dialogTypes.MULTILINGUAL_INTRO, {
        onClose: showMultilingualTab,
        toContinue: () => showAddLanguageModal(),
        triggerId: 'website-settings',
        formData,
        showPromptForce,
        locales,
        mainLocale,
        roadmapUrl,
      });
    } else {
      showAddLanguageModal();
    }
  }, [formData, showPromptForce, locales, mainLocale, requestBlockData, showMultilingualTab]);

  const isMobile = Boolean(bowser.mobile);
  const usedLanguages = getUsedLanguages(locales, languages, countries);

  return (
    <MultilingualSection
      usedLanguages={usedLanguages}
      mainLocale={mainLocale}
      isMobile={isMobile}
      deleteLang={deleteLang}
      setMainLang={setMainLang}
      addLanguage={addLanguage}
      captions={captions}
      formData={formData}
      hasAbilityToAddLocale={Boolean(availableLocales.length)}
      areAiFeaturesAvailable={areAiFeaturesAvailable}
      tutorialUrl={tutorialUrl}
    />
  );
}

export default MultilingualTabContainer;
